import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import Select from 'react-select';
import JSZip from 'jszip';
import Layout from '../../components/Layout';
import DatePicker from '../../components/DatePicker';
import Loading from '../../components/Loading';
import { API_ENDPOINTS } from '../../config/api';
import { useToast } from '../../components/Toast';
import {
  fetchCoupons,
  // fetchAllCoupons,
  fetchCouponsQr,
  setFilters,
  setCurrentPage,
  setSelectedCoupons,
  toggleSelectCoupon,
  selectAllCoupons,
  deselectAllCoupons,
  deleteCouponsBatch,
  createCoupon,
  openCreateModal,
  closeCreateModal,
  upsertCouponsFromSocket,
  markCouponUsedFromSocket,
  removeCouponsFromSocket,
} from '../../store/slices/couponSlice';
import {
  fetchCouponTypes,
  removeCouponTypeFromSocket,
  upsertCouponTypeFromSocket,
} from '../../store/slices/couponTypeSlice';
import { useCouponTypeSocket, useIssuedCouponSocket } from '../../hooks/useSocketListeners';
import './Coupon.css';

const ITEMS_PER_PAGE = 7;

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.replace(/-/g, '/');
};

const Coupon = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const toast = useToast();
  const {
    coupons,
    allCoupons,
    selectedCoupons,
    currentPage,
    filters,
    isLoading,
    isCreateModalOpen,
  } = useSelector((state) => state.coupon);
  const { couponTypes } = useSelector((state) => state.couponType);
  const { user } = useSelector((state) => state.auth);
  const rawAccountType = user?.cmAccountType;
  const isClient = rawAccountType === 'client';
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [qrItems, setQrItems] = useState([]);
  const [filterCollapsed, setFilterCollapsed] = useState(true);
  const handleStartDateFilterChange = (value) => {
    if (filters.endDate && new Date(value) > new Date(filters.endDate)) {
      toast.error('開始日は終了日より後に設定できません。');
      return;
    }
    handleFilterChange('startDate', value);
  };

  const handleEndDateFilterChange = (value) => {
    if (filters.startDate && new Date(value) < new Date(filters.startDate)) {
      toast.error('終了日は開始日より前に設定できません。');
      return;
    }
    handleFilterChange('endDate', value);
  };
  
  const svgBase64ToPngBytes = async (base64Data) => {
    const dataUrl = `data:image/svg+xml;base64,${base64Data}`;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = dataUrl;
    });

    const canvas = document.createElement('canvas');
    const width = img.naturalWidth || 300;
    const height = img.naturalHeight || 300;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Failed to convert SVG to PNG'))), 'image/png');
    });

    const buf = await blob.arrayBuffer();
    return new Uint8Array(buf);
  };

  const sanitizeFilePart = (value) =>
    String(value || '')
      .trim()
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, '_');

  const downloadCouponImagesZip = async (coupons, couponTypeLabel) => {
    try {
      const zip = new JSZip();
      const now = new Date();
      const folderName = `coupons_${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(
        now.getHours()
      ).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(
        now.getSeconds()
      ).padStart(2, '0')}`;
      const folder = zip.folder(folderName);
      
      for (const coupon of coupons) {
        if (!coupon?.qrBase64 || !coupon?.couponRawCode) continue;

        const safeCouponType =
          sanitizeFilePart(coupon.couponType) ||
          sanitizeFilePart(couponTypeLabel) ||
          'coupon';

        const base64Data = String(coupon.qrBase64).replace(/^data:[^;]+;base64,/, '');

        let decodedText = '';
        try {
          decodedText = atob(base64Data).slice(0, 200).trim();
        } catch {
          decodedText = '';
        }

        const looksLikeSvg =
          decodedText.startsWith('<?xml') ||
          decodedText.startsWith('<svg') ||
          decodedText.includes('<svg');

        if (looksLikeSvg) {
          const pngBytes = await svgBase64ToPngBytes(base64Data);
          folder.file(`${safeCouponType}_${sanitizeFilePart(coupon.couponRawCode)}.png`, pngBytes);
        } else {
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          folder.file(`${safeCouponType}_${sanitizeFilePart(coupon.couponRawCode)}.png`, bytes);
        }
      }
      
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const downloadUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${folderName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('ZIPファイルをダウンロードしました');
    } catch (error) {
      toast.error('ZIPファイルの作成に失敗しました');
    }
  };

  const [formData, setFormData] = useState({
    ctId: null,
    quantity: 1,
    isPrint: false,
  });

  const [isQrLoading, setIsQrLoading] = useState(false);

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'すべて' },
      { value: '有効', label: '有効' },
      { value: '保存済み', label: '保存済み' },
      { value: '使用済み', label: '使用済み' },
      { value: '期限切れ', label: '期限切れ' },
    ],
    []
  );

  const typeOptions = useMemo(() => [
    { value: 'all', label: 'すべて' },
    ...couponTypes.map(t => ({ value: t.name, label: `${t.name}` }))
  ], [couponTypes]);
  const couponTypeOptions = useMemo(() => {
    if (!couponTypes || couponTypes.length === 0) {
      return [];
    }
    const seen = new Set();
    return couponTypes
      .filter(t => t.ctFlagShip === true || t.sellable === true)
      .map(t => {
        const id = t.id || t.ctId;
        const name = t.name || t.ctName || 'Unknown';
        return { 
          value: id, 
          label: name 
        };
      })
      .filter(opt => {
        if (opt.value == null || seen.has(opt.value)) {
          return false;
        }
        seen.add(opt.value);
        return true;
      });
  }, [couponTypes]);

  const selectedStatus = statusOptions.find(o => o.value === filters.status) || statusOptions[0];
  const selectedType = typeOptions.find(o => o.value === filters.type) || typeOptions[0];

  const totalPages = Math.ceil(coupons.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentData = coupons.slice(startIndex, endIndex);

  const isCouponExpired = (coupon) => {
    if (!coupon.expiryDate) return false;
    const d = new Date(coupon.expiryDate);
    if (Number.isNaN(d.getTime())) return false;
    d.setHours(23, 59, 59, 999);
    return d < new Date();
  };

  useEffect(() => {
    // dispatch(fetchAllCoupons());
    dispatch(fetchCoupons(filters));
    dispatch(fetchCouponTypes());
  }, []);

  const handleCouponSocketEvent = useCallback(
    (e) => {
      const action = e?.action;
      if (action === 'created') {
        const coupons = Array.isArray(e?.data?.coupons) ? e.data.coupons : [];
        dispatch(upsertCouponsFromSocket(coupons));
        return;
      }
      if (action === 'used') {
        dispatch(markCouponUsedFromSocket(e?.data));
        return;
      }
      if (action === 'deleted') {
        dispatch(removeCouponsFromSocket(e?.data));
        return;
      }

      // Fallback (unknown payload): re-fetch
      const currentFilters = store.getState().coupon.filters;
      dispatch(fetchCoupons(currentFilters));
    },
    [dispatch, store]
  );

  useIssuedCouponSocket(handleCouponSocketEvent);

  const handleCouponTypeSocketEvent = useCallback(
    (e) => {
      const action = e?.action;
      const payload = e?.couponType ?? e?.data;
      if (action === 'deleted') {
        dispatch(removeCouponTypeFromSocket(payload));
        return;
      }
      if (action === 'created' || action === 'updated') {
        dispatch(upsertCouponTypeFromSocket(payload));
        return;
      }
      dispatch(fetchCouponTypes());
    },
    [dispatch]
  );

  useCouponTypeSocket(handleCouponTypeSocketEvent);

  useEffect(() => {
    if (isCreateModalOpen) {
      setFormData({
        ctId: null,
        quantity: 1,
        isPrint: false,
      });
    }
  }, [isCreateModalOpen]);

  useEffect(() => {
    if (isCreateModalOpen) {
      dispatch(fetchCouponTypes());
    }
  }, [isCreateModalOpen, dispatch]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleSelectCoupon = (id) => {
    dispatch(toggleSelectCoupon(id));
  };

  const selectableCoupons = useMemo(
    () => coupons.filter((c) => c.isUsed === '有効' && !isCouponExpired(c)),
    [coupons]
  );

  const allListSelected = useMemo(() => {
    if (selectableCoupons.length === 0) return false;
    return selectableCoupons.every((coupon) => selectedCoupons.includes(coupon.id));
  }, [selectableCoupons, selectedCoupons]);

  const someListSelected = useMemo(() => {
    if (selectableCoupons.length === 0) return false;
    const selectedCount = selectableCoupons.filter((coupon) =>
      selectedCoupons.includes(coupon.id)
    ).length;
    return selectedCount > 0 && selectedCount < selectableCoupons.length;
  }, [selectableCoupons, selectedCoupons]);

  const selectAllCheckboxRef = useRef(null);

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someListSelected;
    }
  }, [someListSelected]);

  const handleSelectAll = () => {
    if (allListSelected) {
      dispatch(setSelectedCoupons([]));
      return;
    }
    dispatch(setSelectedCoupons(selectableCoupons.map((c) => c.id)));
  };

  const handleSearch = () => {
    dispatch(fetchCoupons(filters));
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      couponCode: '',
      startDate: '',
      endDate: '',
      status: 'all',
      type: 'all',
    };
    dispatch(setFilters(clearedFilters));
    dispatch(setCurrentPage(1));
    dispatch(fetchCoupons(clearedFilters));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      dispatch(setCurrentPage(page));
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) {
      pages.push('...');
    } else {
      start = 2;
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) {
      pages.push('...');
    }

    pages.push(totalPages);

    return pages;
  };

  const handleCreateCoupon = () => {
    dispatch(openCreateModal());
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formData.ctId) {
      alert('クーポン種類を選択してください');
      return;
    }
    if (!formData.quantity || formData.quantity < 1) {
      alert('数量は1以上である必要があります');
      return;
    }
    const result = await dispatch(createCoupon({ 
      ctId: formData.ctId, 
      quantity: formData.quantity,
      isPrint: formData.isPrint || false,
    }));
    if (createCoupon.fulfilled.match(result)) {
      toast.success('クーポンを作成しました');
      // Update UI immediately without refetch.
      if (Array.isArray(result.payload?.coupons) && result.payload.coupons.length > 0) {
        dispatch(upsertCouponsFromSocket(result.payload.coupons));
      }
      
      if (result.payload?.isPrint && result.payload?.coupons && result.payload.coupons.length > 0) {
        const mappedCoupons = result.payload.coupons.map(c => ({
          ...c,
          qrBase64: c.qrBase64 || c.couponQrBase64,
        }));
        await downloadCouponImagesZip(mappedCoupons, result.payload?.couponType);
      }
    } else {
      const msg =
        result?.payload ||
        result?.error?.message ||
        'クーポンの作成に失敗しました';
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    if (selectedCoupons.length === 0) return;
    if (!window.confirm('選択したクーポンを削除しますか？')) return;
    try {
      await dispatch(deleteCouponsBatch(selectedCoupons)).unwrap();
      toast.success('削除しました');
    } catch (e) {
      toast.error(e?.message || '削除に失敗しました');
    }
  };

  const looksLikeSvgBase64 = (base64Data) => {
    try {
      const txt = atob(String(base64Data).replace(/^data:[^;]+;base64,/, '')).slice(0, 200).trim();
      return txt.startsWith('<?xml') || txt.startsWith('<svg') || txt.includes('<svg');
    } catch {
      return false;
    }
  };

  const toImageDataUrl = (base64Data) => {
    const cleaned = String(base64Data || '').replace(/^data:[^;]+;base64,/, '');
    const isSvg = looksLikeSvgBase64(cleaned);
    return isSvg ? `data:image/svg+xml;base64,${cleaned}` : `data:image/png;base64,${cleaned}`;
  };

  const handleDownload = async () => {
    try {
      if (isQrLoading) return;
      if (!selectedCoupons || selectedCoupons.length === 0) {
        toast.error('クーポンを選択してください');
        return;
      }
      setIsQrLoading(true);

      const rows = await dispatch(fetchCouponsQr(selectedCoupons)).unwrap();
      if (!rows.length) {
        toast.error('QRデータが見つかりません');
        return;
      }
      await downloadCouponImagesZip(rows, selectedType?.label || selectedType?.value || 'coupon');
    } catch (e) {
      toast.error(e?.message || 'ダウンロードに失敗しました');
    } finally {
      setIsQrLoading(false);
    }
  };

  const handleView = async () => {
    try {
      if (isQrLoading) return;
      if (!selectedCoupons || selectedCoupons.length === 0) {
        toast.error('クーポンを選択してください');
        return;
      }
      setIsQrLoading(true);

      const rows = await dispatch(fetchCouponsQr(selectedCoupons)).unwrap();
      if (!rows.length) {
        toast.error('QRデータが見つかりません');
        return;
      }

      setQrItems(rows);
      setIsQrModalOpen(true);
    } catch (e) {
      toast.error(e?.message || '表示に失敗しました');
    } finally {
      setIsQrLoading(false);
    }
  };

  // const handlePrint = async () => {
  //   try {
  //     if (isQrLoading) return;
  //     if (!selectedCoupons || selectedCoupons.length === 0) {
  //       toast.error('クーポンを選択してください');
  //       return;
  //     }
  //     setIsQrLoading(true);

  //     const rows = await dispatch(fetchCouponsQr(selectedCoupons)).unwrap();
  //     if (!rows.length) {
  //       toast.error('QRデータが見つかりません');
  //       return;
  //     }

  //     const w = window.open('', '_blank');
  //     if (!w) {
  //       toast.error('ポップアップがブロックされました');
  //       return;
  //     }
  //     const html = `
  //       <html>
  //         <head>
  //           <title>QR Print</title>
  //           <style>
  //             @media print { .noprint { display:none; } }
  //             body { font-family: Arial, sans-serif; padding: 16px; }
  //             .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  //             .card { border: 1px solid #ddd; border-radius: 8px; padding: 10px; }
  //             .code { font-size: 10px; color: #555; margin-top: 6px; word-break: break-all; }
  //             img { width: 180px; height: 180px; object-fit: contain; background: #fff; }
  //           </style>
  //         </head>
  //         <body>
  //           <div class="grid">
  //             ${rows
  //               .map(
  //                 (r) => `
  //                   <div class="card">
  //                     <img src="${toImageDataUrl(r.qrBase64)}" />
  //                     <div class="code">${String(r.couponRawCode || '')}</div>
  //                   </div>
  //                 `
  //               )
  //               .join('')}
  //           </div>
  //           <script>
  //             window.onload = function() { window.print(); };
  //           </script>
  //         </body>
  //       </html>
  //     `;
  //     w.document.open();
  //     w.document.write(html);
  //     w.document.close();
  //   } catch (e) {
  //     toast.error(e?.message || '印刷に失敗しました');
  //   } finally {
  //     setIsQrLoading(false);
  //   }
  // };

  const handleCloseQrModal = () => {
    setIsQrModalOpen(false);
    setQrItems([]);
    dispatch(deselectAllCoupons());
  };

  return (
    <Layout>
      <div className="coupon">
        <div className="coupon__card">
          <h1 className="coupon__title">クーポン作成と履歴確認</h1>

          <div className="coupon__scroll-area">
          <button className="coupon__create-btn" onClick={handleCreateCoupon}>
            クーポンを作成
            <span className="coupon__create-btn-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="coupon__create-btn-icon-svg">
                <path fillRule="evenodd" d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
              </svg>
            </span>
          </button>

          <div className={`coupon__filters-wrapper ${filterCollapsed ? 'coupon__filters-wrapper--collapsed' : ''}`}>
            <button
              type="button"
              className="coupon__filters-toggle"
              onClick={() => setFilterCollapsed((v) => !v)}
              aria-expanded={!filterCollapsed}
            >
              <span className="coupon__filters-toggle-text">フィルター</span>
              <span className="coupon__filters-toggle-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </span>
            </button>
            <div className="coupon__filters">
              {!isClient && (
                <div className="coupon__filter-row">
                  <label className="coupon__filter-label">クライアントID</label>
                  <input
                    type="text"
                    className="coupon__filter-input"
                    placeholder="クライアントID"
                    value={filters.couponCode}
                    onChange={(e) => handleFilterChange('couponCode', e.target.value)}
                  />
                </div>
              )}

              <div className="coupon__filter-row">
                <label className="coupon__filter-label">期間</label>
                <div className="coupon__filter-date-range">
                  <DatePicker
                    value={filters.startDate}
                    onChange={handleStartDateFilterChange}
                  />
                  <span className="coupon__filter-date-separator">~</span>
                  <DatePicker
                    value={filters.endDate}
                    onChange={handleEndDateFilterChange}
                  />
                </div>
              </div>

              <div className="coupon__filter-row">
                <label className="coupon__filter-label">状態</label>
                <Select
                  className="coupon__filter-select-container"
                  classNamePrefix="coupon__filter-select-container"
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={(opt) => handleFilterChange('status', opt.value)}
                  maxMenuHeight={160}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              <div className="coupon__filter-row">
                <label className="coupon__filter-label">種類</label>
                <Select
                  className="coupon__filter-select-container"
                  classNamePrefix="coupon__filter-select-container"
                  options={typeOptions}
                  value={selectedType}
                  onChange={(opt) => handleFilterChange('type', opt.value)}
                  maxMenuHeight={230}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />

<button type="button" className="coupon__filter-clear-btn" onClick={handleClearFilters} disabled={isLoading}>
                  クリア
                </button>
                <button type="button" className="coupon__search-btn" onClick={handleSearch} disabled={isLoading}>
                  {isLoading ? '検索中...' : '検索'}
                </button>
                
              </div>
            </div>
          </div>

          {isQrLoading && (
            <Loading fullScreen text="QRコードを処理中..." />
          )}
          {!isQrLoading && isLoading && (
            <Loading text="クーポンを読み込み中..." />
          )}
          {!isLoading && (
            <div className="coupon__table-wrapper">
              <table className="coupon__table">
                <thead className="coupon__table-head">
                  <tr className="coupon__table-row">
                    <th className="coupon__table-th coupon__table-th--select">
                      <label className="coupon__checkbox">
                        <input
                          type="checkbox"
                          checked={allListSelected}
                          ref={selectAllCheckboxRef}
                          onChange={handleSelectAll}
                          className="coupon__checkbox-input"
                        />
                        <span className="coupon__checkbox-custom">
                          <svg viewBox="0 0 24 24" className="coupon__checkbox-icon">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                          </svg>
                        </span>
                      </label>
                    </th>
                    <th className="coupon__table-th">クーポンコード</th>
                    <th className="coupon__table-th">クライアントID</th>
                    <th className="coupon__table-th">クーポン種類</th>
                    <th className="coupon__table-th">クーポン名</th>
                    <th className="coupon__table-th">発行日</th>
                    <th className="coupon__table-th">有効期限</th>
                    <th className="coupon__table-th">ステータス</th>
                  </tr>
                </thead>
                <tbody className="coupon__table-body">
                  {(() => {
                    const rows = [...currentData];
                    while (rows.length < ITEMS_PER_PAGE) rows.push(null);
                    return rows.slice(0, ITEMS_PER_PAGE).map((coupon, index) => {
                      if (!coupon) {
                        return (
                          <tr key={`empty-${currentPage}-${index}`} className="coupon__table-row coupon__table-row--empty">
                            <td className="coupon__table-td coupon__table-td--select" />
                            <td className="coupon__table-td" />
                            <td className="coupon__table-td" />
                            <td className="coupon__table-td" />
                            <td className="coupon__table-td" />
                            <td className="coupon__table-td" />
                            <td className="coupon__table-td" />
                            <td className="coupon__table-td" />
                          </tr>
                        );
                      }
                      const hasClientId = !!coupon.userId;
                      const isUsed = coupon.isUsed !== '有効';
                      const isExpired = isCouponExpired(coupon);
                      const isSelectable = !hasClientId && !isUsed && !isExpired;
                      return (
                        <tr key={coupon.id} className="coupon__table-row">
                          <td className="coupon__table-td coupon__table-td--select">
                            <label
                              className={`coupon__checkbox ${
                                !isSelectable ? 'coupon__checkbox--disabled' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedCoupons.includes(coupon.id)}
                                onChange={() => isSelectable && handleSelectCoupon(coupon.id)}
                                className="coupon__checkbox-input"
                                disabled={!isSelectable}
                              />
                              <span className="coupon__checkbox-custom">
                                <svg viewBox="0 0 24 24" className="coupon__checkbox-icon">
                                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                                </svg>
                              </span>
                            </label>
                          </td>
                          <td className="coupon__table-td">{coupon.couponCode}</td>
                          <td className="coupon__table-td">{coupon.managerId}</td>
                          <td className="coupon__table-td">{coupon.couponType}</td>
                          <td className="coupon__table-td">{coupon.couponName ?? '-'}</td>
                          <td className="coupon__table-td">{formatDateDisplay(coupon.issuedAt)}</td>
                          <td className="coupon__table-td">{coupon.expiryDate ? formatDateDisplay(coupon.expiryDate) : '-'}</td>
                          <td className="coupon__table-td">
                            <span className={`coupon__status coupon__status--${
                              coupon.isUsed === '有効' ? 'valid' :
                              coupon.isUsed === '保存済み' ? 'saved' :
                              coupon.isUsed === '使用済み' ? 'used' :
                              coupon.isUsed === '無効' ? 'invalid' :
                              coupon.isUsed === '期限切れ' ? 'expired' : 'default'
                            }`}>
                              {coupon.isUsed}
                            </span>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {/* {!isLoading && coupons.length === 0 && (
            <div className="coupon__empty">
              <p>データがありません</p>
            </div>
          )} */}

           {/* {selectedCoupons.length > 0 && (
            <div className="coupon__selection-info">
              <span className="coupon__selection-count">
                {selectedCoupons.length} 件選択中
              </span>
              <button
                type="button"
                className="coupon__deselect-btn"
                onClick={() => dispatch(deselectAllCoupons())}
              >
                選択解除
              </button>
            </div>
          )} */}

          {!isLoading && coupons.length > 0 && (
            <div className="coupon__pagination">
              <button
                className="coupon__pagination-btn coupon__pagination-btn--nav"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="10" height="10">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>
              </button>

              {renderPagination().map((page, index) => (
                <button
                  key={index}
                  className={`coupon__pagination-btn ${
                    page === currentPage ? 'coupon__pagination-btn--active' : ''
                  } ${page === '...' ? 'coupon__pagination-btn--dots' : ''}`}
                  onClick={() => page !== '...' && handlePageChange(page)}
                  disabled={page === '...'}
                >
                  {page}
                </button>
              ))}

              <button
                className="coupon__pagination-btn coupon__pagination-btn--nav"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="10" height="10">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          )}

<div className="coupon__actions">
            {!isClient && (<button
              className="coupon__action-btn coupon__action-btn--danger"
              onClick={handleDelete}
              disabled={selectedCoupons.length === 0}
            >
              削除
            </button>)}

            <button
              className="coupon__action-btn coupon__action-btn--primary"
              onClick={handleDownload}
              disabled={selectedCoupons.length === 0}
            >
              ダウンロード
              <svg viewBox="0 0 24 24" className="coupon__action-btn-icon">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
              </svg>
            </button>
            <button
              className="coupon__action-btn coupon__action-btn--info"
              onClick={handleView}
              disabled={selectedCoupons.length === 0}
            >
              見る
              <svg viewBox="0 0 24 24" className="coupon__action-btn-icon">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
              </svg>
            </button>
          </div>      
          {selectedCoupons.length > 0 && (
            <div className="coupon__selection-info">
              <span className="coupon__selection-count">
                {selectedCoupons.length} 件選択中
              </span>
              <button
                type="button"
                className="coupon__deselect-btn"
                onClick={() => dispatch(deselectAllCoupons())}
              >
                選択解除
              </button>
            </div>
          )}
          </div>
        </div>

        {isCreateModalOpen && (
          <div className="coupon__modal-overlay">
            <div className="coupon__modal">
              <div className="coupon__modal-header">
                <h2 className="coupon__modal-title">クーポンを作成</h2>
                <button 
                  className="coupon__modal-close"
                  onClick={() => dispatch(closeCreateModal())}
                >
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
              <form onSubmit={handleCreateSubmit} className="coupon__modal-form">
                <div className="coupon__form-group">
                  <label className="coupon__form-label">クーポン種類</label>
                  <Select
                    className="coupon__form-select-container"
                    options={couponTypeOptions}
                    value={couponTypeOptions.find(opt => opt.value === formData.ctId) || null}
                    onChange={(opt) => setFormData({ ...formData, ctId: opt?.value || null })}
                    placeholder="クーポン種類を選択"
                    isSearchable={true}
                    required
                  />
                  <span className="coupon__form-hint">※ 作成するクーポンの種類を選択してください</span>
                </div>

                <div className="coupon__form-group">
                  <label className="coupon__form-label">数量</label>
                  <div className="coupon__form-input-group">
                    <input
                      type="number"
                      step="1"
                      min="1"
                      className="coupon__form-input coupon__form-input--number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || null })}
                      placeholder="1"
                      required
                    />
                    <span className="coupon__form-input-suffix">枚</span>
                  </div>
                  <span className="coupon__form-hint">※ 作成するクーポンの枚数を入力してください</span>
                </div>

                <div className="coupon__form-group coupon__form-group--checkbox">
                  <label className="coupon__form-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isPrint}
                      onChange={(e) => setFormData({ ...formData, isPrint: e.target.checked })}
                      className="coupon__form-checkbox"
                    />
                    <span className="coupon__form-checkbox-custom">
                      <svg viewBox="0 0 24 24" className="coupon__form-checkbox-icon">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                      </svg>
                    </span>
                    <span>作成後にQRコード画像をダウンロード</span>
                  </label>
                  <span className="coupon__form-hint">※ チェックすると、作成完了後にQRコード画像がZIPファイルとして自動的にダウンロードされます</span>
                </div>

                <div className="coupon__modal-actions">
                  <button 
                    type="button" 
                    className="coupon__modal-btn coupon__modal-btn--cancel"
                    onClick={() => dispatch(closeCreateModal())}
                  >
                    キャンセル
                  </button>
                  <button 
                    type="submit" 
                    className="coupon__modal-btn coupon__modal-btn--submit"
                    disabled={isLoading}
                  >
                    {isLoading ? '作成中...' : '作成'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isQrModalOpen && (
          <div className="coupon__modal-overlay" onClick={handleCloseQrModal}>
            <div className="coupon__modal coupon__qr-modal" onClick={(e) => e.stopPropagation()}>
              <div className="coupon__modal-header">
                <h2 className="coupon__modal-title">QRコード一覧</h2>
                <button className="coupon__modal-close" onClick={handleCloseQrModal}>
                  ×
                </button>
              </div>
              <div className="coupon__qr-modal-body">
                <div className="coupon__qr-grid">
                  {qrItems.map((r) => {
                    const related = coupons.find(
                      (c) => String(c.couponCode || '') === String(r.couponRawCode || '')
                    );
                    const qrOverlay =
                      related?.isUsed === '使用済み'
                        ? 'used'
                        : related?.isUsed === '保存済み'
                          ? 'saved'
                          : null;
                    return (
                      <div
                        key={r.id}
                        className={`coupon__qr-card ${
                          qrOverlay === 'used'
                            ? 'coupon__qr-card--used'
                            : qrOverlay === 'saved'
                              ? 'coupon__qr-card--saved'
                              : ''
                        }`}
                      >
                        <div className="coupon__qr-image-wrapper">
                          {qrOverlay && (
                            <div className="coupon__qr-used-badge">
                              <div
                                className={`coupon__qr-used-badge-tag ${
                                  qrOverlay === 'saved'
                                    ? 'coupon__qr-used-badge-tag--saved'
                                    : ''
                                }`}
                              >
                                {qrOverlay === 'used' ? '使用済み' : '保存済み'}
                              </div>
                            </div>
                          )}
                          <img
                            src={toImageDataUrl(r.qrBase64)}
                            alt={r.couponRawCode}
                            className="coupon__qr-image"
                          />
                        </div>
                        <div className="coupon__qr-code">
                          {String(r.couponRawCode || '')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Coupon;
